/**
 * Machine-readable error codes shared by the API and the web client.
 *
 * The API returns them in the uniform error envelope
 * `{ error: { code, message, correlationId } }` (see
 * `apps/api/src/common/filters/global-exception.filter.ts`); the DB triggers
 * from migration 0113 raise the same strings as their exception message so
 * the service layer can map a trigger violation 1:1 onto a 409.
 *
 * Clients branch on `code`, never on `message` (the message is human copy and
 * may change). Codes are additive: never rename or reuse one.
 */
export const ErrorCodes = {
  /** A `task_relations` row would link a personal-team task with a team task (§2.2 P9). */
  crossSpaceRelationForbidden: 'cross_space_relation_forbidden',
  /** `parent_task_id` must point at a task in the same project (§2.2 P9). */
  parentMustBeSameProject: 'parent_must_be_same_project',
  /** A personal team can only ever have its owner as a member (§2.2 P5). */
  personalTeamSingleMember: 'personal_team_is_single_member',
  /** Invitations, agents and webhooks can't be created on a personal team. */
  personalTeamFeatureForbidden: 'personal_team_feature_forbidden',
  /**
   * The task is invisible to the caller or doesn't exist. My Work answers
   * every foreign / invisible / OUTSTAFF-hidden task with this 404 — never a
   * 403 or 409 — so routes can't be used to probe task existence.
   */
  taskNotFound: 'task_not_found',
  /**
   * The Personal inbox project (`system_kind='inbox'`, migration 0114) can't be
   * archived, deleted, renamed, moved, shared or wired to GitHub; its
   * `system_kind` never changes. Raised by the API (409) and the DB triggers.
   */
  systemProjectLocked: 'system_project_locked',
} as const;

export type ErrorCode = (typeof ErrorCodes)[keyof typeof ErrorCodes];

const ERROR_CODE_VALUES: ReadonlySet<string> = new Set(Object.values(ErrorCodes));

/** Narrow an unknown `error.code` from an API envelope to a known shared code. */
export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && ERROR_CODE_VALUES.has(value);
}
